import { IS_EA } from '@ben/shared/constants';
import { getEmployerCalculatedValue } from '@ben/shared/hooks/mutations/use-mutation-employer-calculated-value';
import { useDebouncedCallback } from '@mantine/hooks';
import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { camelCase, isString } from 'lodash';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getCalculatedValue } from '../../api/ea/benefits';

interface UseCalculatedValueOptions {
  benefitId?: number;
  enrollmentId?: string;
  benefitForm: unknown;
  setBenefitForm: (
    updater: (previous: Record<string, unknown>) => Record<string, unknown>
  ) => void;
  setDontTriggerMutation: (value: boolean) => void;
  delay?: number;
}

interface CalculatedValueResponse {
  key: string;
  value: unknown;
}

const isCalculatedValueResponse = (
  data: unknown
): data is CalculatedValueResponse => {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const responseData = data as Record<string, unknown>;
  return isString(responseData.key);
};

const getCalculatedValueFormKeys = (responseKey: string) => {
  const normalizedKey = camelCase(responseKey);
  return normalizedKey === responseKey
    ? [responseKey]
    : [responseKey, normalizedKey];
};

export const useCalculatedValue = ({
  benefitId,
  enrollmentId,
  benefitForm,
  setBenefitForm,
  setDontTriggerMutation,
  delay = 300,
}: UseCalculatedValueOptions) => {
  const { t } = useTranslation();
  const [isPending, setIsPending] = useState(false);

  const getEACalculatedValue = useCallback(
    (fieldId: string, benefitForm: unknown, signal?: AbortSignal) => {
      if (!benefitId) {
        throw new Error('benefitId is required for EA calculated values');
      }
      return getCalculatedValue(
        Number(benefitId),
        Number(fieldId),
        benefitForm,
        signal
      );
    },
    [benefitId]
  );

  const getECCCalculatedValue = useCallback(
    (fieldId: string, benefitForm: unknown, signal?: AbortSignal) => {
      if (!enrollmentId) {
        throw new Error('enrollmentId is required for ECC calculated values');
      }
      return getEmployerCalculatedValue({
        enrollmentId,
        fieldId,
        data: benefitForm,
        signal,
      });
    },
    [enrollmentId]
  );

  const getCalculatedValueFunction = IS_EA
    ? getEACalculatedValue
    : getECCCalculatedValue;

  const mutation = useMutation({
    mutationFn: async ({
      fieldId,
      signal,
    }: {
      fieldId: string;
      signal?: AbortSignal;
    }) => {
      setIsPending(true);
      return await getCalculatedValueFunction(fieldId, benefitForm, signal);
    },
    onSuccess: (data) => {
      if (!isCalculatedValueResponse(data)) {
        setIsPending(false);
        return;
      }

      setBenefitForm((previous: Record<string, unknown>) => {
        const updatedForm = { ...previous };
        const formKeys = getCalculatedValueFormKeys(data.key);

        for (const formKey of formKeys) {
          updatedForm[formKey] = data.value;
        }

        return {
          ...updatedForm,
        };
      });
      setDontTriggerMutation(true);
      setIsPending(false);
    },
    onError: (error) => {
      setIsPending(false);
      const drop = isAxiosError(error)
        ? (error.response?.data as { drop?: string[] } | undefined)?.drop
        : undefined;
      const errorMessage = drop?.[0];
      toast.error(errorMessage ?? t('errors.serverError'));
    },
  });

  const triggerMutation = useDebouncedCallback(
    (fieldId: string, signal?: AbortSignal) => {
      mutation.mutate({ fieldId, signal });
    },
    delay
  );

  return {
    triggerMutation,
    isPending,
  };
};
